import { useState } from "react";

import styles from "./Settings.module.css";
import { Button } from "../../components/Button";
import { ApiError } from "../../data/api/client";
import { clearDevboxImageCache } from "../../data/api/repositories";
import type { ApiDevboxCacheClear } from "../../data/api/types";
import { Icon } from "../../icons";
import { relTime } from "../../lib/time";

// The devbox image cache, and the one thing an operator can do to it
// (repositories.md §Devbox image).
//
// A repository that builds its devbox from a Dockerfile gets one image per
// commit, and every task on that commit reuses it. Nothing tracks the base it
// derives `FROM`, so a repository pinned to `node:24-slim` keeps the copy of
// that tag its last build saw however far upstream moves — and a step that
// installed packages over the network keeps whatever it installed. Clearing is
// the way out of both: the next task builds again, and builds without reusing
// anything the host still holds. It also takes the images off every runner it
// can reach, which is the half that gives the disk back.
//
// The rebuild outlives the click. It is consumed by a build, not by the button,
// so until one runs this says so rather than reporting a rebuild that has not
// happened.

export type DevboxImageProps = {
  /** When the cache was cleared, if no build has honoured it yet. */
  readonly clearedAt: string | null;
  readonly onCleared: () => void;
  readonly repoId: string;
};

const count = (n: number, noun: string): string =>
  `${n.toString()} ${noun}${n === 1 ? "" : "s"}`;

// One line for the fleet: what went, what stayed, and where it could not.
const runnerSummary = (runners: ApiDevboxCacheClear["runners"]): string => {
  if (runners.length === 0) {
    return "There are no runners to clear.";
  }
  const answered = runners.filter((runner) => runner.error === null);
  const removed = answered.reduce((sum, runner) => sum + runner.removed, 0);
  const kept = answered.reduce((sum, runner) => sum + runner.kept, 0);
  const lines = [
    `Removed ${count(removed, "image")} from ${count(answered.length, "runner")}.`,
  ];
  if (kept > 0) {
    lines.push(`Kept ${count(kept, "image")} a sandbox is using.`);
  }
  for (const runner of runners) {
    if (runner.error !== null) {
      lines.push(`Couldn't clear ${runner.hostname}: ${runner.error}.`);
    }
  }
  return lines.join(" ");
};

export const DevboxImage = ({
  clearedAt,
  onCleared,
  repoId,
}: DevboxImageProps) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  const clear = (): void => {
    setBusy(true);
    setError(null);
    setSummary(null);
    void clearDevboxImageCache(repoId)
      .then((result) => {
        setSummary(runnerSummary(result.runners));
        onCleared();
      })
      .catch((err: unknown) =>
        setError(
          err instanceof ApiError
            ? err.message
            : "Couldn't clear the image cache.",
        ),
      )
      .finally(() => setBusy(false));
  };

  const pending = clearedAt === null ? null : Date.parse(clearedAt);

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Devbox image</h3>
      <div className={styles.card}>
        <div className={styles.row}>
          <div className={styles.grow}>
            <div className={styles.label}>Clear image cache</div>
            <div className={styles.help}>
              {pending === null
                ? "Sandboxes reuse the image built for their commit. Clearing removes this repository's images from every runner and makes the next task build a fresh one, without the host's layer cache and with the base image pulled again."
                : `Cleared ${relTime(pending)}. The next task on this repository rebuilds the image; nothing has yet.`}
            </div>
          </div>
          <Button disabled={busy} onClick={clear} size="sm" variant="ghost">
            <Icon.Refresh size={13} /> Clear
          </Button>
        </div>

        <div className={styles.row}>
          <span className={styles.help}>
            Only repositories that build from a <code>devbox.dockerfile</code>
            {" have an image to clear — one that names a prebuilt "}
            <code>devbox.image</code> pulls it instead.
          </span>
        </div>

        {summary ? (
          <div className={styles.row}>
            <span className={styles.help}>{summary}</span>
          </div>
        ) : null}

        {error ? (
          <div className={styles.row}>
            <span
              className={styles.help}
              style={{ color: "var(--needs-input)" }}
            >
              {error}
            </span>
          </div>
        ) : null}
      </div>
    </section>
  );
};
